import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getHolidays, createHoliday, deleteHoliday,
  getLeaveBalancesSummary, initLeaveBalances, updateLeaveBalance
} from '../services/api';
import { Plus, Trash2, CalendarDays, Users, RefreshCw, X, Edit2, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const LEAVE_TYPES = ['casual','sick','earned','maternity','paternity','unpaid','comp_off'];
const DEFAULTS    = { casual:12, sick:12, earned:15, maternity:90, paternity:7, unpaid:0, comp_off:0 };

const typeColor = (t) =>
  t === 'national'  ? 'bg-red-50 text-red-600'   :
  t === 'regional'  ? 'bg-blue-50 text-blue-700'  :
  t === 'optional'  ? 'bg-amber-50 text-amber-700' :
  'bg-gray-100 text-gray-500';

// ── Add Holiday Modal ─────────────────────
const HolidayModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name        : '',
    date        : '',
    holiday_type: 'national',
    description : '',
    is_optional : false
  });

  const mutation = useMutation({
    mutationFn: createHoliday,
    onSuccess: () => {
      toast.success('Holiday added');
      queryClient.invalidateQueries(['holidays']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to add holiday')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">Add Holiday</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); mutation.mutate(form); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
              placeholder="e.g. Republic Day"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select value={form.holiday_type} onChange={e => setForm({ ...form, holiday_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="national">National</option>
              <option value="regional">Regional</option>
              <option value="optional">Optional / Restricted</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Optional note"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="is_optional" checked={form.is_optional}
              onChange={e => setForm({ ...form, is_optional: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded" />
            <label htmlFor="is_optional" className="text-sm text-gray-700">Optional holiday (worker's choice)</label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Add Holiday'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Init Leave Balances Modal ─────────────
const InitModal = ({ year, onClose }) => {
  const queryClient = useQueryClient();
  const [allocs, setAllocs] = useState({ ...DEFAULTS });

  const mutation = useMutation({
    mutationFn: () => initLeaveBalances({ year, allocations: allocs }),
    onSuccess: (res) => {
      toast.success(res.data.message);
      queryClient.invalidateQueries(['leave-balances-summary']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Initialization failed')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Initialize Leave Balances</h2>
            <p className="text-xs text-gray-400 mt-0.5">Sets opening balances for all active workers for {year}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-3 mb-5">
          {LEAVE_TYPES.map(lt => (
            <div key={lt} className="flex items-center justify-between">
              <label className="text-sm text-gray-700 capitalize">{lt.replace('_', ' ')} Leave</label>
              <input type="number" min="0" value={allocs[lt]}
                onChange={e => setAllocs({ ...allocs, [lt]: parseInt(e.target.value) || 0 })}
                className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mb-4">Existing balances will be skipped (not overwritten).</p>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {mutation.isPending ? 'Initializing...' : 'Initialize All Workers'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Inline Balance Edit ───────────────────
const BalanceEditRow = ({ id, allocated, used, onSave }) => {
  const [val, setVal] = useState(allocated);
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => updateLeaveBalance(id, { allocated: val }),
    onSuccess: () => {
      toast.success('Balance updated');
      queryClient.invalidateQueries(['leave-balances-summary']);
      setEditing(false);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Update failed')
  });

  const balance = allocated - used;

  if (!editing) {
    return (
      <div className="flex items-center gap-1 group">
        <span className="text-sm font-medium text-gray-900">{balance}</span>
        <span className="text-xs text-gray-400">({used}/{allocated})</span>
        <button onClick={() => setEditing(true)}
          className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-300 hover:text-blue-500 transition-opacity">
          <Edit2 size={11} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input type="number" min="0" value={val} onChange={e => setVal(parseInt(e.target.value) || 0)}
        className="w-14 px-1.5 py-0.5 border border-blue-300 rounded text-xs text-center focus:outline-none" />
      <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
        className="p-0.5 text-green-600 hover:text-green-700"><Check size={14} /></button>
      <button onClick={() => setEditing(false)}
        className="p-0.5 text-gray-400 hover:text-gray-600"><X size={14} /></button>
    </div>
  );
};

// ── Main Page ─────────────────────────────
const LeaveSetup = () => {
  const queryClient = useQueryClient();
  const [tab,            setTab]            = useState('holidays');
  const [year,           setYear]           = useState(new Date().getFullYear());
  const [showHolModal,   setShowHolModal]   = useState(false);
  const [showInitModal,  setShowInitModal]  = useState(false);

  const { data: holidays = [], isLoading: loadingH } = useQuery({
    queryKey: ['holidays', year],
    queryFn : () => getHolidays(year).then(r => r.data.data)
  });

  const { data: balances = [], isLoading: loadingB } = useQuery({
    queryKey: ['leave-balances-summary', year],
    queryFn : () => getLeaveBalancesSummary(year).then(r => r.data.data),
    enabled : tab === 'balances'
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHoliday,
    onSuccess : () => { toast.success('Holiday removed'); queryClient.invalidateQueries(['holidays']); },
    onError   : (err) => toast.error(err.response?.data?.message || 'Delete failed')
  });

  // Group holidays by month
  const byMonth = holidays.reduce((acc, h) => {
    const m = new Date(h.date).getMonth();
    if (!acc[m]) acc[m] = [];
    acc[m].push(h);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Leave Setup</h1>
          <p className="text-sm text-gray-400 mt-0.5">Holiday calendar and leave balance management</p>
        </div>
        <div className="flex gap-2">
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {tab === 'holidays' && (
            <button onClick={() => setShowHolModal(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} />
              Add Holiday
            </button>
          )}
          {tab === 'balances' && (
            <button onClick={() => setShowInitModal(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
              <RefreshCw size={16} />
              Initialize {year}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[{ id: 'holidays', label: `Holiday Calendar (${holidays.length})` },
          { id: 'balances', label: 'Leave Balances' }
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── HOLIDAYS TAB ── */}
      {tab === 'holidays' && (
        <div className="space-y-3">
          {loadingH ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <CalendarDays size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">No holidays defined for {year}.</p>
              <button onClick={() => setShowHolModal(true)}
                className="mt-3 text-sm text-blue-600 hover:underline">Add the first holiday</button>
            </div>
          ) : (
            Object.entries(byMonth)
              .sort(([a], [b]) => a - b)
              .map(([month, list]) => (
                <div key={month} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{MONTHS[parseInt(month)]}</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {list.map(h => (
                      <div key={h.id} className="flex items-center justify-between px-5 py-3">
                        <div className="flex items-center gap-4">
                          <div className="text-center w-10">
                            <p className="text-lg font-bold text-gray-900 leading-none">
                              {new Date(h.date).getDate()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(h.date).getDay()]}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{h.name}</p>
                            {h.description && <p className="text-xs text-gray-400 mt-0.5">{h.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium capitalize ${typeColor(h.holiday_type)}`}>
                            {h.holiday_type}
                          </span>
                          {h.is_optional && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">Optional</span>
                          )}
                          <button onClick={() => { if (window.confirm(`Remove "${h.name}"?`)) deleteMutation.mutate(h.id); }}
                            className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {/* ── BALANCES TAB ── */}
      {tab === 'balances' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loadingB ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : balances.length === 0 ? (
            <div className="text-center py-16">
              <Users size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">No leave balances for {year}.</p>
              <button onClick={() => setShowInitModal(true)}
                className="mt-3 text-sm text-blue-600 hover:underline">Initialize all workers</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-400 px-5 py-3 sticky left-0 bg-gray-50">Worker</th>
                    {LEAVE_TYPES.map(lt => (
                      <th key={lt} className="text-center text-xs font-medium text-gray-400 px-4 py-3 capitalize whitespace-nowrap">
                        {lt.replace('_', ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {balances.map(row => (
                    <tr key={row.candidate_id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 sticky left-0 bg-white hover:bg-gray-50">
                        <p className="text-sm font-medium text-gray-900">{row.candidate_name}</p>
                        <p className="text-xs text-gray-400">{row.employee_code}</p>
                      </td>
                      {LEAVE_TYPES.map(lt => {
                        const b = row.balances?.[lt];
                        return (
                          <td key={lt} className="px-4 py-3 text-center">
                            {b ? (
                              <BalanceEditRow
                                id={b.id}
                                allocated={b.allocated}
                                used={b.used}
                              />
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showHolModal  && <HolidayModal onClose={() => setShowHolModal(false)} />}
      {showInitModal && <InitModal year={year} onClose={() => setShowInitModal(false)} />}
    </div>
  );
};

export default LeaveSetup;
